import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  screenRecordingService,
  type UploadVideoChunkRequest,
  type UploadVideoChunkData,
} from '@/services/api';

interface ScreenRecordingState {
  isRecording: boolean;
  isUploading: boolean;
  chunkIndex: number;
  totalChunks: number;
  error: string | null;
  uploadProgress: {
    currentChunk: number;
    totalChunks: number;
  } | null;
  finalUploadData: UploadVideoChunkData | null;
}

const initialState: ScreenRecordingState = {
  isRecording: false,
  isUploading: false,
  chunkIndex: 0,
  totalChunks: 0,
  error: null,
  uploadProgress: null,
  finalUploadData: null,
};

export const uploadVideoChunk = createAsyncThunk(
  'screenRecording/uploadVideoChunk',
  async (params: UploadVideoChunkRequest, { rejectWithValue }) => {
    try {
      const response = await screenRecordingService.uploadVideoChunk(params);
      return response;
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : 'Failed to upload video chunk';
      return rejectWithValue(message);
    }
  }
);

const screenRecordingSlice = createSlice({
  name: 'screenRecording',
  initialState,
  reducers: {
    startRecording: state => {
      state.isRecording = true;
      state.chunkIndex = 0;
      state.totalChunks = 0;
      state.error = null;
      state.uploadProgress = null;
      state.finalUploadData = null;
    },
    stopRecording: state => {
      state.isRecording = false;
    },
    incrementChunkIndex: state => {
      state.chunkIndex += 1;
    },
    setTotalChunks: (state, action: PayloadAction<number>) => {
      state.totalChunks = action.payload;
    },
    setUploadProgress: (
      state,
      action: PayloadAction<{
        currentChunk: number;
        totalChunks: number;
      } | null>
    ) => {
      state.uploadProgress = action.payload;
    },
    clearError: state => {
      state.error = null;
    },
    resetRecordingState: () => initialState,
  },
  extraReducers: builder => {
    builder
      .addCase(uploadVideoChunk.pending, state => {
        state.isUploading = true;
        state.error = null;
      })
      .addCase(uploadVideoChunk.fulfilled, (state, action) => {
        state.isUploading = false;
        if (action.payload.data) {
          state.finalUploadData = action.payload.data;
        }
        if (state.uploadProgress) {
          state.uploadProgress.currentChunk =
            action.payload.statusCode === 200
              ? state.uploadProgress.totalChunks
              : state.uploadProgress.currentChunk + 1;
        }
      })
      .addCase(uploadVideoChunk.rejected, (state, action) => {
        state.isUploading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  startRecording,
  stopRecording,
  incrementChunkIndex,
  setTotalChunks,
} = screenRecordingSlice.actions;

export default screenRecordingSlice.reducer;
